import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, DollarSign } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { useTable } from '../../context/TableContext';
import { useCart } from '../../context/CartContext';

const CustomerPayBill: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { orders, updateOrders, tables, updateTables } = useData();
  const { user, logout } = useAuth();
  const { selectedTable, clearTable } = useTable();
  const { clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transactionId, setTransactionId] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [logoutCountdown, setLogoutCountdown] = useState(5);

  const handleBack = () => {
    setCurrentScreen('customer-home');
  };

  // Get only unpaid orders from current session (current table)
  const userOrders = orders.filter(order => 
    order.customerId === user?.customerId && 
    order.isPaid !== true &&
    order.tableId === selectedTable?.id
  );
  const totalBillAmount = userOrders.reduce((sum, order) => sum + order.amount, 0);

  const handlePayBill = async () => {
    if (userOrders.length === 0) {
      setErrorMessage('You have no pending orders to pay.');
      setShowErrorModal(true);
      return;
    }

    if (paymentMethod === 'upi' && !transactionId.trim()) {
      setErrorMessage('Please enter your UPI transaction ID.');
      setShowErrorModal(true);
      return;
    }

    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mark all orders as paid
    const updatedOrders = orders.map(order => 
      userOrders.find(uo => uo.id === order.id)
        ? { 
            ...order, 
            isPaid: true,
            paymentMethod: paymentMethod,
            transactionId: paymentMethod === 'upi' ? transactionId : null
          }
        : order
    );
    updateOrders(updatedOrders);
    
    // Also update each order in Firestore
    const { FirestoreService } = await import('../../firebase/firestore');
    for (const order of userOrders) {
      await FirestoreService.updateOrder(order.id, {
        isPaid: true,
        paymentMethod: paymentMethod,
        transactionId: paymentMethod === 'upi' ? transactionId : null
      });
    }

    // Save customer billing record to Firestore with enhanced error handling
    console.log('🔍 CustomerPayBill: Checking conditions for billing record creation...');
    console.log('🔍 CustomerPayBill: user exists:', !!user);
    console.log('🔍 CustomerPayBill: user.customerId exists:', !!user?.customerId);
    console.log('🔍 CustomerPayBill: selectedTable exists:', !!selectedTable);
    console.log('🔍 CustomerPayBill: userOrders.length:', userOrders.length);
    
    if (user && user.customerId && selectedTable) {
      try {
        console.log('🔄 CustomerPayBill: Starting billing record creation...');
        console.log('👤 CustomerPayBill: User data:', {
          customerId: user.customerId,
          name: user.name,
          email: user.email,
          role: user.role
        });
        console.log('🏠 CustomerPayBill: Table data:', {
          id: selectedTable.id,
          number: selectedTable.tableNumber,
          location: selectedTable.location,
          sessionStartTime: selectedTable.sessionStartTime
        });
        console.log('📋 CustomerPayBill: Orders data:', {
          orderCount: userOrders.length,
          totalAmount: totalBillAmount,
          orderIds: userOrders.map(o => o.id)
        });

        const totalItems = userOrders.reduce((sum, order) => 
          sum + order.items.reduce((s, item) => s + item.quantity, 0), 0
        );
        
        const sessionEndTime = new Date().toISOString();
        const sessionDuration = selectedTable.sessionStartTime 
          ? Math.round((new Date(sessionEndTime).getTime() - new Date(selectedTable.sessionStartTime).getTime()) / 60000)
          : null;

        const billingRecord = {
          customerId: user.customerId,
          customerName: user.name,
          customerEmail: user.email || null,
          tableId: selectedTable.id,
          tableNumber: selectedTable.tableNumber,
          tableLocation: selectedTable.location,
          totalAmount: totalBillAmount,
          paymentMethod: paymentMethod,
          transactionId: paymentMethod === 'upi' ? transactionId : null,
          orderIds: userOrders.map(order => order.id),
          numberOfOrders: userOrders.length,
          totalItems: totalItems,
          paidAt: sessionEndTime,
          sessionStartTime: selectedTable.sessionStartTime,
          sessionEndTime: sessionEndTime,
          sessionDurationMinutes: sessionDuration,
          orderDetails: userOrders.map(order => ({
            orderId: order.id,
            orderAmount: order.amount,
            orderDate: order.date,
            orderStatus: order.status,
            items: order.items.map(item => ({
              productName: item.productName,
              quantity: item.quantity,
              price: item.price,
              total: item.price * item.quantity
            }))
          })),
          // Calculate bill breakdown
          subtotal: totalBillAmount / 1.15, // Assuming 15% total tax/service
          gst: (totalBillAmount / 1.15) * 0.05, // 5% GST
          serviceCharge: (totalBillAmount / 1.15) * 0.10, // 10% Service charge
          // Session metadata
          tableCleanedAt: sessionEndTime,
          billingStatus: 'completed',
          // Add timestamp for debugging
          createdAt: sessionEndTime,
          // Add browser info for debugging
          userAgent: navigator.userAgent,
          // Add session type for debugging
          sessionType: 'customer_temporary'
        };
        
        console.log('💾 CustomerPayBill: Billing record prepared:', billingRecord);
        console.log('🔄 CustomerPayBill: Attempting to save to Firestore...');
        
        // Check Firebase connection before saving
        const { auth } = await import('../../firebase/config');
        console.log('🔐 CustomerPayBill: Firebase auth state:', {
          currentUser: auth.currentUser ? 'Authenticated' : 'Not authenticated',
          uid: auth.currentUser?.uid || 'N/A',
          email: auth.currentUser?.email || 'N/A'
        });
        
        const recordId = await FirestoreService.addCustomerBillingRecord(billingRecord);
        
        if (recordId) {
          console.log('✅ CustomerPayBill: Billing record saved successfully!');
          console.log('🆔 CustomerPayBill: Record ID:', recordId);
          console.log('📊 CustomerPayBill: Record details:', {
            customerId: billingRecord.customerId,
            customerName: billingRecord.customerName,
            totalAmount: billingRecord.totalAmount,
            paymentMethod: billingRecord.paymentMethod,
            numberOfOrders: billingRecord.numberOfOrders,
            totalItems: billingRecord.totalItems
          });
          
          // Dispatch a custom event to notify admin panel of new billing record
          window.dispatchEvent(new CustomEvent('newBillingRecord', {
            detail: { recordId, billingRecord }
          }));
          
        } else {
          console.error('❌ CustomerPayBill: Failed to save billing record - no ID returned');
          console.error('💡 CustomerPayBill: This might indicate a Firestore rules issue');
          console.error('📖 CustomerPayBill: Check CUSTOMER_BILLING_DEBUG_FIX.md for solutions');
          
          // Still continue with the payment process, but log the issue
          setErrorMessage('Payment completed, but billing record may not be saved. Please contact support if needed.');
        }
      } catch (error) {
        console.error('❌ CustomerPayBill: Error saving billing record:', error);
        
        if (error instanceof Error) {
          console.error('❌ CustomerPayBill: Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
          
          // Check for specific Firebase errors
          if (error.message.includes('Missing or insufficient permissions')) {
            console.error('🔒 CustomerPayBill: FIRESTORE SECURITY RULES ERROR');
            console.error('💡 CustomerPayBill: The customerBillingHistory collection requires updated security rules');
            console.error('📖 CustomerPayBill: See CUSTOMER_BILLING_DEBUG_FIX.md for the solution');
            console.error('🔧 CustomerPayBill: Quick fix - Update Firestore rules to allow create: if true for customerBillingHistory');
          } else if (error.message.includes('auth')) {
            console.error('🔐 CustomerPayBill: AUTHENTICATION ERROR');
            console.error('💡 CustomerPayBill: Customer sessions may not have proper Firebase auth');
          } else if (error.message.includes('network') || error.message.includes('offline')) {
            console.error('🌐 CustomerPayBill: NETWORK ERROR');
            console.error('💡 CustomerPayBill: Check internet connection and Firebase configuration');
          }
        }
        
        // Don't fail the payment process due to billing record issues
        console.log('⚠️ CustomerPayBill: Continuing with payment despite billing record error');
      }
    } else {
      console.warn('⚠️ CustomerPayBill: Cannot save billing record - missing required data:');
      console.warn('📊 CustomerPayBill: Data check:', {
        hasUser: !!user,
        hasCustomerId: !!user?.customerId,
        hasSelectedTable: !!selectedTable,
        userDetails: user ? {
          id: user.id,
          customerId: user.customerId,
          name: user.name,
          role: user.role
        } : 'No user',
        tableDetails: selectedTable ? {
          id: selectedTable.id,
          number: selectedTable.tableNumber,
          location: selectedTable.location
        } : 'No table'
      });
    }

    // Clear table data and make it available again
    if (selectedTable) {
      console.log('🧹 CustomerPayBill: Cleaning table after payment:', selectedTable.tableNumber);
      
      const updatedTables = tables.map(t => 
        t.id === selectedTable.id
          ? {
              ...t,
              status: 'available' as const,
              currentCustomerName: undefined,
              currentCustomerId: undefined,
              sessionStartTime: undefined
            }
          : t
      );
      
      // Update tables in local state
      await updateTables(updatedTables);
      
      // Also update the table in Firestore to ensure consistency
      try {
        await FirestoreService.updateTable(selectedTable.id, {
          status: 'available',
          currentCustomerName: undefined,
          currentCustomerId: undefined,
          sessionStartTime: undefined
        });
        console.log('✅ CustomerPayBill: Table cleaned in Firestore:', selectedTable.tableNumber);
      } catch (error) {
        console.error('❌ CustomerPayBill: Error cleaning table in Firestore:', error);
      }
    }

    // Clear table and cart
    clearTable();
    clearCart();
    
    setIsProcessing(false);
    setLogoutCountdown(5); // Reset countdown
    setShowSuccessModal(true);
  };

  const handleSuccessConfirm = async () => {
    setShowSuccessModal(false);
    // Show loading state while logging out
    setIsProcessing(true);
    
    try {
      // Logout user after payment
      await logout();
      // Redirect to welcome page
      setCurrentScreen('welcome');
    } catch (error) {
      console.error('❌ CustomerPayBill: Error during logout:', error);
      // Even if logout fails, redirect to welcome
      setCurrentScreen('welcome');
    }
  };

  // Auto-logout countdown effect
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (showSuccessModal && logoutCountdown > 0) {
      interval = setInterval(() => {
        setLogoutCountdown(prev => prev - 1);
      }, 1000);
    } else if (showSuccessModal && logoutCountdown === 0) {
      // Auto logout when countdown reaches 0
      handleSuccessConfirm();
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showSuccessModal, logoutCountdown]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button onClick={handleBack} className="p-2 text-gray-400 hover:text-white mr-4 transition-colors">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Pay Bill & Exit
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {userOrders.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="food-card text-center py-12">
              <div className="text-6xl mb-4">🍽️</div>
              <h2 className="text-2xl font-bold text-white mb-2">No Orders Yet</h2>
              <p className="text-gray-400 mb-6">You haven't placed any orders. Start ordering to see your bill here.</p>
              <motion.button onClick={() => setCurrentScreen('customer-home')} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn-primary py-3 px-8">
                Browse Menu
              </motion.button>
            </motion.div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="food-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-3">📋</div>
                  <h2 className="text-2xl font-bold text-white">Your Orders</h2>
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {userOrders.map((order) => (
                    <div key={order.id} className="bg-slate-700 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-white font-bold text-lg">Order #{order.id}</p>
                          <p className="text-gray-400 text-sm">{new Date(order.date).toLocaleString()}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            Table: {order.tableNumber} • {order.tableLocation}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-400 font-bold text-xl">${order.amount.toFixed(2)}</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${
                            order.status === 'delivered' ? 'bg-green-500 bg-opacity-20 text-green-400' :
                            order.status === 'shipped' ? 'bg-yellow-500 bg-opacity-20 text-yellow-400' :
                            order.status === 'pending' ? 'bg-blue-500 bg-opacity-20 text-blue-400' :
                            'bg-red-500 bg-opacity-20 text-red-400'
                          }`}>
                            {order.status === 'delivered' ? 'served' :
                             order.status === 'shipped' ? 'ready' :
                             order.status === 'pending' ? 'preparing' :
                             order.status === 'cancelled' ? 'cancelled' :
                             order.status}
                          </span>
                        </div>
                      </div>
                      <div className="border-t border-gray-600 pt-3 mt-3">
                        <p className="text-gray-400 text-xs mb-2">Items:</p>
                        <div className="space-y-1">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-300">{item.productName} x {item.quantity}</span>
                              <span className="text-white">${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="food-card border-2 border-green-600">
                <div className="flex items-center mb-6">
                  <DollarSign className="text-green-400 mr-3" size={32} />
                  <h2 className="text-2xl font-bold text-white">Total Bill Summary</h2>
                </div>
                <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-xl p-6 mb-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-gray-300">
                      <span>Number of Orders</span>
                      <span className="font-semibold">{userOrders.length}</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>Total Items</span>
                      <span className="font-semibold">{userOrders.reduce((sum, order) => sum + order.items.reduce((s, item) => s + item.quantity, 0), 0)}</span>
                    </div>
                    <div className="border-t border-green-700 pt-3 flex justify-between items-center">
                      <span className="text-white font-bold text-2xl">Total Amount</span>
                      <span className="text-green-400 font-bold text-4xl">${totalBillAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Debug Button - Remove in production */}
                  <div className="mb-4">
                    <button
                      onClick={async () => {
                        console.log('🔍 DEBUGGING CUSTOMER PAYMENT DATA');
                        const { debugCustomerPaymentData } = await import('../../utils/debugCustomerPayment');
                        debugCustomerPaymentData();
                        
                        // Also log current component state
                        console.log('🔄 Current Component State:');
                        console.log('👤 User:', user);
                        console.log('🪑 Selected Table:', selectedTable);
                        console.log('📋 User Orders:', userOrders);
                        console.log('💰 Total Bill Amount:', totalBillAmount);
                        console.log('💳 Payment Method:', paymentMethod);
                        console.log('🆔 Transaction ID:', transactionId);
                      }}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                    >
                      🔍 Debug Payment Data (Check Console)
                    </button>
                  </div>
                  
                  <div>
                    <label className="block text-base font-semibold text-gray-300 mb-3">Payment Method *</label>
                    <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full px-4 py-4 bg-slate-700 border-2 border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base">
                      <option value="cash">Cash Payment</option>
                      <option value="card">Card Payment</option>
                      <option value="upi">UPI Payment</option>
                    </select>
                  </div>

                  {paymentMethod === 'upi' && (
                    <div>
                      <label className="block text-base font-semibold text-gray-300 mb-3">Transaction ID *</label>
                      <input type="text" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="Enter UPI transaction ID" className="w-full px-4 py-4 bg-slate-700 border-2 border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base" />
                    </div>
                  )}

                  <motion.button onClick={handlePayBill} disabled={isProcessing} whileHover={{ scale: isProcessing ? 1 : 1.02 }} whileTap={{ scale: isProcessing ? 1 : 0.98 }} className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-5 px-6 rounded-xl transition-all duration-200 text-lg shadow-lg">
                    {isProcessing ? 'Processing Payment...' : `💳 Pay $${totalBillAmount.toFixed(2)} & Exit`}
                  </motion.button>
                  
                  <div className="mt-3 p-3 bg-slate-700 rounded-lg">
                    <p className="text-gray-400 text-sm text-center">
                      ℹ️ After payment, you will be automatically logged out to end your dining session
                    </p>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-800 rounded-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-center">
              <div className="text-6xl mb-3">✅</div>
              <h2 className="text-2xl font-bold text-white">Payment Successful!</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-center mb-2 text-lg">
                Thank you for dining with us!
              </p>
              <p className="text-white text-center mb-4 text-2xl font-bold">
                Total Paid: ${totalBillAmount.toFixed(2)}
              </p>
              
              {/* Payment Method Info */}
              <div className="bg-slate-700 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Payment Method:</span>
                  <span className="text-white font-medium">
                    {paymentMethod === 'cash' ? '💵 Cash' : 
                     paymentMethod === 'card' ? '💳 Card' : 
                     '📱 UPI'}
                  </span>
                </div>
                {paymentMethod === 'upi' && transactionId && (
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-gray-400">Transaction ID:</span>
                    <span className="text-white font-mono text-xs">{transactionId}</span>
                  </div>
                )}
              </div>

              <div className="bg-green-900 bg-opacity-30 border border-green-500 rounded-lg p-4 mb-4">
                <p className="text-green-300 text-center text-sm mb-2">
                  ✅ Payment completed successfully!
                </p>
                {selectedTable && (
                  <p className="text-green-400 text-center text-sm mb-2">
                    🧹 {selectedTable.tableNumber} has been cleaned and is now available
                  </p>
                )}
                <p className="text-gray-400 text-center text-xs">
                  Your dining session has ended
                </p>
              </div>

              <div className="bg-orange-900 bg-opacity-30 border border-orange-500 rounded-lg p-4 mb-6">
                <p className="text-orange-300 text-center text-sm mb-2">
                  🚪 You will be automatically logged out in
                </p>
                <p className="text-orange-400 text-center text-3xl font-bold mb-2">
                  {logoutCountdown}s
                </p>
                <p className="text-gray-400 text-center text-xs">
                  We hope to see you again soon!
                </p>
              </div>

              <div className="space-y-3">
                <motion.button
                  onClick={handleSuccessConfirm}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl text-lg"
                >
                  Logout Now
                </motion.button>
                
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setLogoutCountdown(5); // Reset countdown if user cancels
                  }}
                  className="w-full bg-slate-600 hover:bg-slate-500 text-white font-medium py-3 rounded-xl text-sm"
                >
                  Stay Logged In
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowErrorModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-800 rounded-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-center">
              <div className="text-6xl mb-3">⚠️</div>
              <h2 className="text-2xl font-bold text-white">Oops!</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-center mb-6 text-lg">
                {errorMessage}
              </p>
              <motion.button
                onClick={() => setShowErrorModal(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full btn-primary py-4 text-lg"
              >
                Got it
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default CustomerPayBill;
